

























































































































import {
  computed,
  defineComponent,
  ref,
  onMounted,
  ssrRef,
  useFetch,
  watch,
  useRouter,
  useContext,
} from "@nuxtjs/composition-api";
import SkeletonLoader from "~/components/SkeletonLoader/index.vue";
import ProductLoadItem from "~/components/SkeletonLoader/ProductLoadItem.vue";
import { CacheTagPrefix, useCache } from "@vue-storefront/cache";
import { usePageStore } from "~/stores/page";
import { useUiHelpers, useUiState, useSearchProducts } from "~/composables";
import { useAddToCart } from "~/helpers/cart/addToCart";
import { useCategoryStore } from "~/modules/catalog/category/stores/category";

import type { ProductInterface } from "~/modules/GraphQL/types";
import type { SortingModel } from "~/modules/catalog/category/composables/useFacet/sortingOptions";
import type { Pagination } from "~/composables/types";

import { changeImage, decodeUid, isMobile } from "~/helpers";
import LazyComponent from "v-lazy-component/vue2";
import { useTraverseCategory } from "~/modules/catalog/category/helpers/useTraverseCategory";
import { findCategoryByUid } from "~/modules/catalog/category/helpers";

import { useClevertapStore } from "~/stores/clevertap";
import { SelectedFiltersInterface } from "../filters/useFilters";
import { useCategoryPageStore } from "~/modules/catalog/category/stores/categoryPage";
import type { RemovableFilterInterface } from "~/modules/catalog/category/components/filters/useFilters";
import CategoryNavbar from "~/modules/catalog/category/components/navbar/CategoryNavbarNew.vue";
import FilterNavBar from "~/modules/catalog/category/components/filters/FilterNavBar/FilterNavBar.vue";
import CategoryNavbarMobile from "~/modules/catalog/category/components/navbar/CategoryNavbarMobile.vue";
import { debounce } from "lodash-es";

export default defineComponent({
  name: "LandingFilterPage",
  components: {
    LazyComponent,
    SkeletonLoader,
    ProductLoadItem,
    CategoryNavbar,
    FilterNavBar,
    CategoryNavbarMobile,
    CategoryEmptyResults: () =>
      import("~/modules/catalog/category/components/CategoryEmptyResults.vue"),
    CategoryProductGrid: () =>
      import(
        "~/modules/catalog/category/components/views/CategoryProductGrid.vue"
      ),
    LandingPageFilters: () =>
      import(
        "~/modules/catalog/category/components/landingPages/LandingPageFilters.vue"
      ),
    LandingFilterByPositions: () =>
      import(
        "~/modules/catalog/category/components/landingPages/LandingFilterByPositions.vue"
      ),
    LandingPageFiltersMobile: () =>
      import(
        "~/modules/catalog/category/components/landingPages/LandingPageFiltersMobile.vue"
      ),
  },
  props: {
    item: {
      type: Object,
      default: () => {},
    },
  },

  setup(props) {
    const { routeData } = usePageStore();
    const router = useRouter();
    const { fetchProducts, loading: loadingProduct } = useSearchProducts();

    const { activeCategoryById } = useTraverseCategory();
    const activeCategoryByUid = computed(() => {
      return activeCategoryById(decodeUid(routeData?.uid));
    });

    const clevertapStore = useClevertapStore();

    const { addTags } = useCache();
    const uiHelpers = useUiHelpers();

    const products = ssrRef<ProductInterface[]>([]);
    const sortBy = ref<SortingModel>({ selected: "", options: [] });
    const pagination = ref<Pagination>({});
    const productContainerElement = ref<HTMLElement | null>(null);

    const { addItemToCart } = useAddToCart();
    const loading = ref(false);
    const categoryPageStore = useCategoryPageStore();
    const categoryStore = useCategoryStore();

    const isOpenFilterMobile = ref(false);
    const categoryUid = routeData.uid as string;
    const categoryId = decodeUid(categoryUid);
    const categoryTree = computed(() => categoryStore.categories);

    const positions = computed(() => {
      if (props.item?.positions && props.item.positions.length) {
        return props.item.positions;
      }

      const positionsToCheck = ["sizeguide", "category_ids", "final_price"];
      const foundPositions = [];

      if (props.item) {
        positionsToCheck.forEach((key) => {
          if (key in props.item) {
            foundPositions.push(key);
          }
        });
      }

      return foundPositions;
    });

    const filterByPosition = (position) => {
      return props.item?.[position] ? props.item?.[position] : [];
    };

    const firstPosition = computed(() => {
      if (positions.value && positions.value.length > 0) {
        return filterByPosition(positions.value[0]);
      }
      return [];
    });

    const selectedFiltersLanding = ref<SelectedFiltersInterface>({});
    const categoryIdKey = computed(() =>
      String(props.item?.category_id || "9999")
    );
    const currentPage = computed(
      () => categoryPageStore.getLandingPage(categoryIdKey.value) || 1
    );

    onMounted(async () => {
      // fetch();

      // Track product list view when products are loaded - single source of truth for tracking
      if (products.value.length > 0 && activeCategoryByUid.value) {
        setTimeout(() => {
          trackProductListView();
        }, 500); // Small delay to ensure everything is loaded
      }

      isPriceLoaded.value = true;
    });
    const { app } = useContext();
    const scrollToTop = () => {
      const isMobile = app && app.$device && app.$device.isMobile;
      if (process.client && typeof window !== "undefined") {
        setTimeout(() => {
          const popupElement = document.querySelector(
            ".position-popup-fix-top"
          );

          const heightPopup = isMobile
            ? 0
            : popupElement?.getBoundingClientRect()?.height || 0;
          const appHeaderElement = document.querySelector(
            ".position-appheader"
          );
          const appHeaderHeight =
            appHeaderElement?.getBoundingClientRect()?.height || 0;
          const minusHeight = heightPopup + appHeaderHeight;

          const filterPositionsElement =
            document.querySelector(".filter-positions");

          if (filterPositionsElement) {
            const rect = filterPositionsElement.getBoundingClientRect();
            const absoluteTop = window.pageYOffset + rect.top;

            window.scrollTo({
              top: absoluteTop - minusHeight,
              behavior: "smooth",
            });
          } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        }, 100);
      }
    };

    const getParamFilter = () => {
      let routerQuery;
      if (process?.client) {
        routerQuery = router?.resolve(
          (window?.location?.pathname + window?.location?.search)?.slice(1)
        )?.route?.query;
      } else {
        routerQuery = router?.currentRoute?.query;
      }

      let paramFilter: any = {};

      if (routerQuery && Object.keys(routerQuery).length > 0) {
        if (routerQuery?.size) {
          paramFilter.sizeIds = Array.isArray(routerQuery.size)
            ? routerQuery?.size
            : [routerQuery?.size];
        }

        if (routerQuery?.price) {
          paramFilter.priceRanger = Array.isArray(routerQuery.price)
            ? routerQuery?.price
            : routerQuery?.price.split("_");
        }

        if (routerQuery?.mastercolor) {
          paramFilter.colorIds = Array.isArray(routerQuery.mastercolor)
            ? routerQuery?.mastercolor
            : [routerQuery?.mastercolor];
        }

        if (routerQuery?.sort && routerQuery.sort == "created_at_DESC") {
          paramFilter.order = "desc";
          paramFilter.orderby = "created_at";
        }

        if (routerQuery?.sort && routerQuery.sort == "price_ASC") {
          paramFilter.order = "asc";
          paramFilter.orderby = "final_price";
        }

        if (routerQuery?.sort && routerQuery.sort == "price_DESC") {
          paramFilter.order = "desc";
          paramFilter.orderby = "final_price";
        }

        if (routerQuery?.sizeguide && routerQuery.sizeguide.length > 0) {
          paramFilter.sizeGuideIds = [];
          if (props.item?.sizeguide) {
            props.item.sizeguide.forEach((item) => {
              if (routerQuery.sizeguide?.includes(item.key)) {
                item.value?.forEach((value) => {
                  if (value) {
                    paramFilter.sizeGuideIds.push(value);
                  }
                });
              }
            });
          }
        }

        // if (routerQuery?.final_price) {
        //   paramFilter.price = [];
        //   if (props.item?.final_price) {
        //     props.item.final_price.forEach((item) => {
        //       if (routerQuery?.final_price?.includes(item.key)) {
        //         paramFilter.price.push(item);
        //       }
        //     });
        //   }
        // }

        return paramFilter;
      }
    };

    const getParamIds = () => {
      let routerQuery;
      if (process?.client) {
        routerQuery = router?.resolve(
          (window?.location?.pathname + window?.location?.search)?.slice(1)
        )?.route?.query;
      } else {
        routerQuery = router?.currentRoute?.query;
      }
      let paramFilter: any = {};

      if (routerQuery?.category_ids && routerQuery?.category_ids.length > 0) {
        if (props.item?.category_ids) {
          paramFilter.ids = [];
          props.item.category_ids.forEach((item) => {
            if (routerQuery.category_ids?.includes(item.key)) {
              item.value?.forEach((value) => {
                if (value) {
                  paramFilter.ids.push(value);
                }
              });
            }
          });
        }
      } else {
        paramFilter.ids = [props.item?.category_id?.toString()];
      }
      return paramFilter;
    };

    const totalItems = ref(0);
    const fetchInit = () => {
      let routerQuery;
      if (process?.client) {
        routerQuery = router?.resolve(
          (window?.location?.pathname + window?.location?.search)?.slice(1)
        )?.route?.query;
      } else {
        routerQuery = router?.currentRoute?.query;
      }

      if (!routerQuery || Object.keys(routerQuery).length === 0) {
        positions.value.forEach((position) => {
          filterByPosition(position).forEach((category, index) => {
            if (index !== 0) {
              return;
            }
            if (category?.value !== undefined) {
              selectedFiltersLanding.value[position] = [category?.key];
            }
          });
        });
      } else {
        selectedFiltersLanding.value = { ...routerQuery };
        if (
          routerQuery?.category_ids == undefined &&
          props.item?.category_ids
        ) {
          selectedFiltersLanding.value.category_ids =
            props.item.category_ids?.[0]?.key;
        }
        if (routerQuery?.sizeguide == undefined && props.item?.sizeguide) {
          selectedFiltersLanding.value.sizeguide =
            props.item.sizeguide?.[0]?.key;
        }
        if (routerQuery?.final_price == undefined && props.item?.final_price) {
          selectedFiltersLanding.value.final_price =
            props.item.final_price?.[0]?.key;
        }
      }

      uiHelpers.addFilters(selectedFiltersLanding.value, false);
    };

    const { fetch } = useFetch(async () => {
      loading.value = true;
      fetchInit();

      const params = {
        ids: getParamIds()?.ids,
        pageSize: 24 * currentPage.value,
        filters: getParamFilter(),
      };
      const { data, total } = await fetchProducts(params);

      products.value = data || [];
      totalItems.value = total || 0;
      loading.value = false;

      const tags = [{ prefix: CacheTagPrefix.View, value: routeData.uid }];
      const productTags = products.value?.map((product) => ({
        prefix: CacheTagPrefix.Product,
        value: product?.uid,
      }));

      addTags([...tags, ...productTags]);
      // scrollToTop();
    });

    const isPriceLoaded = ref(false);

    // Function to track product list view with Clevertap
    const hasTrackedView = ref(false); // Add flag to prevent duplicate tracking

    const trackProductListView = () => {
      if (
        !activeCategoryByUid.value ||
        !products.value.length ||
        hasTrackedView.value
      )
        return;

      if (process.client && window.clevertap) {
        const categoryName = activeCategoryByUid.value?.name || "";

        // Create tracking data
        const trackData = {
          data: {
            list_id: props.item?.category_id.toString(),
            list_name: categoryName,
          },
          clevertap: window.clevertap,
        };

        // Call Clevertap event and mark as tracked
        clevertapStore.productListViewdCleverTap(trackData);
        hasTrackedView.value = true; // Set flag to prevent duplicate tracking
      }
    };

    const trackProductListFilter = (searchParams: any) => {
      if (!activeCategoryByUid.value) return;

      // Create base tracking data
      const trackData: any = {
        data: {
          list_id: categoryId,
          list_name: activeCategoryByUid.value.name || "",
        },
        clevertap: window.clevertap,
      };

      // Add sort information
      if (searchParams.sort) {
        let sortBy = "default";
        // Handle the sort format like "name_ASC" or "price_DESC"
        const sortParts = searchParams.sort.split("_");
        if (sortParts.length > 0) {
          const [sortField, sortDirection] = sortParts;
          if (sortField === "price" && sortDirection === "ASC") {
            sortBy = "price asc";
          } else if (sortField === "price" && sortDirection === "DESC") {
            sortBy = "price desc";
          } else if (sortField === "created") {
            sortBy = "newest";
          }
        }
        trackData.data.sort_by = sortBy;
      }

      // Process filters
      if (
        searchParams.filters &&
        Object.keys(searchParams.filters).length > 0
      ) {
        const filters = searchParams.filters;
        const filterTypes = [];

        // Category filter
        if (filters.category_id) {
          filterTypes.push("category");

          // Handle category filters as array
          if (Array.isArray(filters.category_id)) {
            filters.category_id.forEach((catId: string, index: number) => {
              // Find category name by ID if possible
              let catName = "";
              if (categoryTree.value) {
                const category = findCategoryByUid(categoryTree.value, catId);
                if (category) {
                  catName = category.name;
                }
              }
              trackData.data[`category_name_${index + 1}`] = catName || catId;
            });
          }
        }

        // Category IDs filter
        if (filters.category_ids) {
          filterTypes.push("categorys");

          // Handle category filters as array
          if (Array.isArray(filters.category_ids)) {
            filters.category_ids.forEach((category: string, index: number) => {
              trackData.data[`categorys_type_${index + 1}`] = category;
            });
          }
        }

        // Size guide filter
        if (filters.sizeguide) {
          filterTypes.push("sizeguide");

          // Handle category filters as array
          if (Array.isArray(filters.sizeguide)) {
            filters.sizeguide.forEach((sizeg: string, index: number) => {
              trackData.data[`sizeguide_type_${index + 1}`] = sizeg;
            });
          }
        }

        // Size filter
        if (filters.size) {
          filterTypes.push("size");

          // Handle size filters as array
          if (Array.isArray(filters.size)) {
            filters.size.forEach((size: string, index: number) => {
              trackData.data[`size_type_${index + 1}`] = size;
            });
          }
        }

        // Color filter
        if (filters.mastercolor) {
          filterTypes.push("color");

          // Handle color filters as array
          if (Array.isArray(filters.mastercolor)) {
            filters.mastercolor.forEach((color: string, index: number) => {
              trackData.data[`color_value_${index + 1}`] = color;
            });
          }
        }

        // Price filter
        if (filters.price) {
          filterTypes.push("price");

          // Handle price range format like "129000_202000"
          if (Array.isArray(filters.price) && filters.price.length > 0) {
            const priceRange = filters.price[0];
            const priceParts = priceRange.split("_");
            if (priceParts.length === 2) {
              const [from, to] = priceParts;
              trackData.data.price_value = `${from} - ${to}`;
            } else {
              trackData.data.price_value = priceRange;
            }
          }
        }

        // Sale filter
        if (filters.discount_percent) {
          filterTypes.push("sale");

          // Handle sale filters as array
          if (Array.isArray(filters.discount_percent)) {
            filters.discount_percent.forEach((sale: string, index: number) => {
              trackData.data[`sale_${index + 1}`] = sale;
            });
          }
        }

        // Add filter_by parameter with comma-separated list of filter types
        if (filterTypes.length > 0) {
          trackData.data.filter_by = filterTypes.join(", ");
        }
      }
      // Call Clevertap event
      clevertapStore.productListFilterCleverTap(trackData);
    };

    const debouncedTrackFilter = debounce((searchParams: any) => {
      trackProductListFilter(searchParams);
    }, 500);

    // Simplify the watch - only check activeCategory, no need to call trackProductListView here
    // as it will be called in onMounted
    watch(
      () => activeCategoryByUid.value,
      (newCategory) => {
        // Don't track here to avoid duplicate events
      }
    );

    const lastTrackedParams = ref('');

    watch(
      () => products.value,
      (newResult, oldResult) => {
        if (process.client && window.clevertap && activeCategoryByUid.value && newResult.length > 0) {
          // Get current search params
          const searchParams = uiHelpers.getFacetsFromURL();
          // Stringify params to compare with last tracked params
          const paramsString = JSON.stringify(searchParams);

          // Check if filters or sort have been applied and it's different from last tracked
          const hasFilters = searchParams.filters && Object.keys(searchParams.filters).length > 0;
          const hasSort = !!searchParams.sort;

          if ((hasFilters || hasSort) && paramsString !== lastTrackedParams.value) {
            lastTrackedParams.value = paramsString;
            debouncedTrackFilter(searchParams);
          }
        }
      },
      { deep: true, immediate: true  },
    );

    const goToPage = (page: number) => {
      uiHelpers.changePage(page, false);
      fetch();
    };

    const doChangeItemsPerPage = (itemsPerPage: number) => {
      uiHelpers.changeItemsPerPage(itemsPerPage, false);
      goToPage(1);
    };

    const onReloadProducts = () => {
      categoryPageStore.resetLandingPage(categoryIdKey.value);
      fetch();
      scrollToTop();
    };

    const removableFilters = ref(<RemovableFilterInterface[]>[]);
    const onRemovableFiltersChanged = (filters) => {
      removableFilters.value = filters;
    };

    const categoryFiltersRef = ref(null);
    const landingFilterByPositionsRef = ref(null);
    const categoryNavbarMobileRef = ref(null);

    const onRemoveFilter = ({ id, value }) => {
      categoryFiltersRef.value?.doRemoveFilter?.({ id, value });
    };
    const onClearAllFilters = () => {
      categoryFiltersRef.value?.doClearFilters?.();
    };

    const onReloadProductsFromPositions = () => {
      // Gọi getIndexActive ở LandingPageFilters
      categoryFiltersRef.value?.getIndexActive?.();
      categoryNavbarMobileRef.value?.initSortValue?.();
      onReloadProducts();
    };

    const onReloadProductsFromFilters = () => {
      // Gọi getIndexActive ở LandingFilterByPositions nếu có
      landingFilterByPositionsRef.value?.getIndexActive?.();
      categoryNavbarMobileRef.value?.initSortValue?.();
      onReloadProducts();
    };

    const isLoadingMore = ref(false);

    const loadMoreProducts = async () => {
      isLoadingMore.value = true;

      const nextPage = currentPage.value + 1;
      categoryPageStore.setLandingPage(categoryIdKey.value, nextPage);

      const params = {
        ids: getParamIds()?.ids,
        pageSize: 24,
        filters: getParamFilter(),
        currentPage: nextPage,
      };

      const { data, total } = await fetchProducts(params);
      const newProducts = data || [];
      products.value = [...products.value, ...newProducts];
      isLoadingMore.value = false;
    };

    return {
      isPriceLoaded,
      ...uiHelpers,
      addItemToCart,
      pagination,
      products,
      sortBy,
      routeData,
      doChangeItemsPerPage,
      productContainerElement,
      currentPage,
      onReloadProducts,
      loadMoreProducts,
      isLoadingMore,
      activeCategoryName: computed(() => activeCategoryByUid.value?.name ?? ""),
      changeImage,
      trackProductListView, // Make tracking function available in template
      loading,
      totalItems,
      loadingProduct,
      onRemovableFiltersChanged,
      firstPosition,
      removableFilters,
      onRemoveFilter,
      onClearAllFilters,
      categoryFiltersRef,
      landingFilterByPositionsRef,
      categoryNavbarMobileRef,
      onReloadProductsFromPositions,
      onReloadProductsFromFilters,
      isOpenFilterMobile,
    };
  },
});
